type BAMSRequest = {
    id: string;
    requestId?: string;
    dateOfRequest?: string;
    branchCode?: string;
    branchName?: string;
    branchId?: string;
    vendor?: string;
    vendorId?: string;
    items?: Array<{
        itemId: string;
        itemCode: string;
        itemName: string;
        itemDescription?: string;
        quantity: number;
        rate?: number;
        amount?: number;
    }>;
    remarks?: string;
    status?: string;
    [key: string]: any;
};

type Branch = {
    id: string;
    branchCode?: string;
    address?: string;
    name?: string;
    [key: string]: any;
};

type Vendor = {
    contactPersonName?: string;
    companyName?: string;
    companyAddress?: string;
    [key: string]: any;
};

type InventoryItem = {
    id: string;
    itemCode?: string;
    itemName?: string;
    itemDescription?: string;
    brandName?: string;
    makeModel?: string;
    rates?: number;
    unitPrice?: number;
    [key: string]: any;
};

type InchargeInfo = {
    name?: string;
    email?: string;
};

// Generate Purchase Order Number
export const generatePurchaseOrderNumber = (): string => {
    const timestamp = Date.now().toString().slice(-7); // Last 7 digits of timestamp
    const randomNum = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `PO-${randomNum}-${timestamp}`;
};

// Format date as DD-MM-YYYY
const formatDateDDMMYYYY = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

// Calculate delivery date (PO date + 7 days)
const calculateDeliveryDate = (poDate: Date): string => {
    const deliveryDate = new Date(poDate);
    deliveryDate.setDate(deliveryDate.getDate() + 7);
    return formatDateDDMMYYYY(deliveryDate);
};

// Format date as DD-MMM-YYYY (e.g., 05-Dec-2025)
const formatDateDDMMMYYYY = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

// Calculate installation deadline (Request Date + 20 days)
const calculateInstallationDeadline = (requestDate: Date): string => {
    const deadline = new Date(requestDate);
    deadline.setDate(deadline.getDate() + 20);
    return formatDateDDMMMYYYY(deadline);
};

type PSOInfo = {
    name?: string;
    email?: string;
    region?: string;
};

type RegionalHeadInfo = {
    name?: string;
    email?: string;
};

// Format BAMS Installation Email (for View Modal Send Email)
export const formatBAMSInstallationEmail = (
    request: BAMSRequest,
    vendor: Vendor | null,
    branch: Branch | null,
    psoInfo: PSOInfo = {},
    requestCategory?: string
): string => {
    const bankName = 'HBL';
    const branchName = branch?.name || request.branchName || 'N/A';
    const branchCode = branch?.branchCode || request.branchCode || 'N/A';
    const branchAddress = branch?.address || request.branchName || 'N/A';

    // Vendor information
    const vendorName = vendor?.contactPersonName || 'Sir/Madam';
    const vendorCompany = vendor?.companyName || request.vendor || 'Vendor Company';

    // PSO information
    const psoName = psoInfo.name || 'PSO';
    const psoRegion = psoInfo.region || 'Region';

    // Calculate installation deadline
    let installationDeadline = 'N/A';
    if (request.dateOfRequest) {
        const requestDate = new Date(request.dateOfRequest);
        installationDeadline = calculateInstallationDeadline(requestDate);
    }

    // Format contact person name
    let formattedContactName = vendorName;
    if (formattedContactName && !formattedContactName.toLowerCase().startsWith('mr.') && !formattedContactName.toLowerCase().startsWith('ms.')) {
        formattedContactName = `Mr. ${formattedContactName}`;
    }

    // Format PSO name
    let formattedPsoName = psoName;
    if (formattedPsoName && !formattedPsoName.toLowerCase().startsWith('mr.') && !formattedPsoName.toLowerCase().startsWith('ms.')) {
        formattedPsoName = `Mr. ${formattedPsoName}`;
    }

    // Determine system type and equipment text
    const category = requestCategory || request.requestCategory || 'New';
    const systemType = 'BAMS System';
    const equipmentText = 'BAMS equipment';

    return `
        <div style="font-family: Arial, sans-serif; max-width: 700px; margin: 0 auto; padding: 20px; line-height: 1.8;">
            <p style="margin-bottom: 20px;">
                Dear ${formattedContactName},<br/>
                M/S ${vendorCompany}
            </p>
            
            <p style="margin-bottom: 15px;">
                In the light of existing SLA, please install New ${systemType} (Free Package) at ${bankName} Branch:<br/>
                <strong>Code: ${branchCode} - ${branchAddress}</strong><br/>
                by ${installationDeadline} (Or On readiness of branch) with Coordination / Information of PSO
            </p>

            <p style="margin-bottom: 20px;">
                Dear ${formattedPsoName},<br/>
                ${psoRegion}
            </p>
            
            <p style="margin-bottom: 20px;">
                Please coordinate with WP&P and Vendor for installation of all ${equipmentText} as per SLA by M/S ${vendorCompany}.
            </p>

            <p style="margin-top: 30px; font-style: italic; color: red;">
                <strong>Note:</strong> "HBL reserves rights to cancel the subject Work Order if the installation is not completed within the stipulated time. The branch may be allocated to other vendor in case of non-compliance of SLA"
            </p>
        </div>
    `;
};


export const formatBAMSEmailBody = (
    request: BAMSRequest,
    vendor: Vendor | null,
    branches: Branch[] = []
): string => {
    // Try to get bank name from branches data or default to HBL
    const bankName = 'HBL'; // Default, can be enhanced to fetch from bank data
    const branchName = request.branchName || 'N/A';
    const branchCode = request.branchCode || 'N/A';
    const branchId = request.branchId || '';

    // Find branch in branches array to get address
    const branch = branches.find(b => b.id === request.branchId || b.branchCode === request.branchCode);
    const branchAddress = branch?.address || request.branchName || 'N/A';

    const vendorName = vendor?.contactPersonName || vendor?.companyName || 'Sir/Madam';
    const vendorCompany = vendor?.companyName || 'Vendor Company';

    const itemsTable = request.items && request.items.length > 0
        ? request.items.map((item, index) => `
            <tr>
                <td>${index + 1}</td>
                <td>${item.itemName || 'N/A'}</td>
                <td>${item.quantity || 0}</td>
                <td>Make Model must as per approved SLA model</td>
                <td>Rates will be charged as per NEW approved SLA rates</td>
            </tr>
        `).join('')
        : '<tr><td colspan="5" style="text-align: center;">No items</td></tr>';

    const htmlBody = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>BAMS Hardware Request</title>
        <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; margin: 0; padding: 20px; }
            .header { background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); color: #333; padding: 20px; border-radius: 8px 8px 0 0; border: 2px solid #dee2e6; }
            .content { background: #ffffff; padding: 20px; border-radius: 0 0 8px 8px; border: 2px solid #dee2e6; border-top: none; }
            .greeting { margin: 20px 0; }
            .hardware-table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            .hardware-table th, .hardware-table td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            .hardware-table th { background-color: #007bff; color: white; }
            .hardware-table td { background-color: #f8f9fa; }
            .instructions { background: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0; border-left: 4px solid #28a745; }
            .instructions ul { margin: 10px 0; padding-left: 20px; }
            .instructions li { margin: 5px 0; }
            .footer { margin-top: 20px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; }
        </style>
    </head>
    <body>
        <div class="header">
            <h2>${bankName} - BAMS Hardware Request</h2>
            <p>Request ID: ${request.requestId || request.id}</p>
            <p>Date: ${request.dateOfRequest ? new Date(request.dateOfRequest).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'N/A'}</p>
        </div>
        
            <div class="content">
                <div class="greeting">
                <p>Dear ${vendorName} M/S ${vendorCompany},</p>
                </div>
            
            <p>As per approval of head Security Services and existing SLA with ${vendorCompany} please procure and install the below mentioned BAMS Hardware and cable at our <strong>${bankName} ${branchName} Branch ${branchCode}${branchId ? ' ' + branchId : ''}</strong>${branchAddress && branchAddress !== branchName ? ' (' + branchAddress + ')' : ''}.</p>
            
            <h3>Hardware Information</h3>
            <table class="hardware-table">
                <thead>
                    <tr>
                        <th>S. No.</th>
                        <th>Hardware Information</th>
                        <th>Quantity</th>
                        <th>Make/Model</th>
                        <th>Amount</th>
                    </tr>
                </thead>
                <tbody>
                    ${itemsTable}
                </tbody>
            </table>
            
            <div class="instructions">
                <h4>Vendor Team Instructions</h4>
                <p>Please ensure the following points need to be accomplished after the installation of said hardware.</p>
                <ul>
                    <li>Complete and proper <strong>Delivery Challan (Make / Quantity / Serial No.)</strong> should be properly filled and <strong>signed from relevant vendor and branch staff.</strong></li>
                    <li>Please confirm when installation has done.</li>
                </ul>
                <p><strong>Payment Terms:</strong> Above all; please note that payment will be made as per the above mentioned hardware only; if any additional hardware would be required then PSO will take separate approval of these items from concerned management prior to invoicing.</p>
            </div>
            
                <div class="footer">
                    <p>Best regards,</p>
                    <p><strong>PSO Team</strong></p>
            </div>
        </div>
    </body>
    </html>
    `;

    return htmlBody;
};

export const formatBAMSPurchaseOrderEmail = (
    request: BAMSRequest,
    vendor: Vendor | null,
    branches: Branch[] = [],
    inchargeInfo: InchargeInfo = {},
    poNumber?: string,
    poDate?: Date,
    inventoryItems: InventoryItem[] = [],
    branchPsoName?: string,
    branchPsoRegion?: string,
    taxPercentage: number = 18
): string => {
    const bankName = 'HBL';
    const branchName = request.branchName || 'N/A';
    const branchCode = request.branchCode || 'N/A';

    // Find branch in branches array to get address
    const branch = branches.find(b => b.id === request.branchId || b.branchCode === request.branchCode);
    const branchAddress = branch?.address || request.branchName || 'N/A';

    // Vendor information
    const vendorName = vendor?.contactPersonName || 'Sir/Madam';
    const vendorCompany = request.vendor || vendor?.companyName || 'Vendor Company';
    const vendorAddress = vendor?.companyAddress || 'N/A';
    const vendorContactPerson = vendor?.contactPersonName || 'N/A';

    // Incharge information
    const buyerName = inchargeInfo.name || 'Incharge';
    const buyerEmail = inchargeInfo.email || 'N/A';

    // Purchase Order details
    const purchaseOrderNumber = poNumber || generatePurchaseOrderNumber();
    const purchaseOrderDate = poDate || new Date();
    const poDateFormatted = formatDateDDMMYYYY(purchaseOrderDate);
    const deliveryDate = calculateDeliveryDate(purchaseOrderDate);

    // Calculate total amount (fetch rates from inventory items if not in request items)
    // Note: rates already includes tax, so we calculate backwards
    let totalBaseAmount = 0;
    let totalTaxAmount = 0;
    let totalAmountWithTax = 0;

    const itemsTable = request.items && request.items.length > 0
        ? request.items.map((item: any, index) => {
            // Try to get rates from item first, then from inventory
            let unitPriceWithTax = item.rate || item.rates || item.unitPrice || 0;
            let makeModel = item.makeModel || item.brandName || 'As per SLA';
            let itemTaxPercentage = taxPercentage; // Default to global tax percentage

            // If rates not in item, fetch from inventory
            if (!unitPriceWithTax && item.itemId) {
                const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                if (inventoryItem) {
                    unitPriceWithTax = inventoryItem.rates || inventoryItem.unitPrice || 0;
                    makeModel = inventoryItem.makeModel || inventoryItem.brandName || makeModel;
                    // Use tax percentage from inventory item if available
                    itemTaxPercentage = inventoryItem.taxPercentage ?? taxPercentage;
                }
            } else if (item.itemId) {
                // Get tax percentage from inventory item even if we have rates
                const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                if (inventoryItem && inventoryItem.taxPercentage !== undefined) {
                    itemTaxPercentage = inventoryItem.taxPercentage;
                }
            }

            const quantity = item.quantity || 0;
            // unitPriceWithTax already includes tax, so calculate backwards
            const taxRate = itemTaxPercentage / 100;
            const totalPriceWithTax = unitPriceWithTax * quantity;
            // Base Price = Price with Tax / (1 + tax rate)
            const basePrice = totalPriceWithTax / (1 + taxRate);
            // Unit Base Price = Base Price / quantity
            const unitBasePrice = quantity > 0 ? basePrice / quantity : 0;
            // Tax = Price with Tax - Base Price
            const tax = totalPriceWithTax - basePrice;

            totalBaseAmount += basePrice;
            totalTaxAmount += tax;
            totalAmountWithTax += totalPriceWithTax;

            // Get item description from item or inventory
            let itemDescription = item.itemDescription || '';
            if (!itemDescription && item.itemId) {
                const inventoryItem = inventoryItems.find(inv => inv.id === item.itemId);
                if (inventoryItem) {
                    itemDescription = inventoryItem.itemDescription || '';
                }
            }

            return `
                <tr>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: center; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${index + 1}</td>
                    <td style="border: 1px solid #000000; padding: 8px; font-size: 11px; color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; max-width: 120px;">${itemDescription || item.itemName || 'N/A'}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: center; font-size: 11px; color: #000000 !important; word-wrap: break-word;">Pcs</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: center; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${quantity}</td>
                    <td style="border: 1px solid #000000; padding: 8px; font-size: 11px; color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; max-width: 100px;">${makeModel}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: right; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${unitPriceWithTax > 0 ? `Rs. ${unitBasePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: right; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${unitPriceWithTax > 0 ? `Rs. ${basePrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: center; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${unitPriceWithTax > 0 ? `${itemTaxPercentage}%` : 'N/A'}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: right; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${unitPriceWithTax > 0 ? `Rs. ${tax.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}</td>
                    <td style="border: 1px solid #000000; padding: 8px; text-align: right; font-size: 11px; color: #000000 !important; word-wrap: break-word;">${unitPriceWithTax > 0 ? `Rs. ${totalPriceWithTax.toLocaleString(undefined, { maximumFractionDigits: 2 })}` : 'As per SLA'}</td>
                </tr>
            `;
        }).join('')
        : '<tr><td colspan="10" style="text-align: center; border: 1px solid #ddd; padding: 8px;">No items</td></tr>';

    // Grand total = total amount with tax
    const grandTotal = totalAmountWithTax;

    // Format contact person name - handle cases where name might have "Mr." prefix
    let formattedContactName = vendorName;
    if (formattedContactName && !formattedContactName.toLowerCase().startsWith('mr.')) {
        formattedContactName = `Mr. ${formattedContactName}`;
    }

    // Format PSO name - handle cases where name might have "Mr." prefix
    let formattedPsoName = branchPsoName || 'PSO';
    if (formattedPsoName && !formattedPsoName.toLowerCase().startsWith('mr.')) {
        formattedPsoName = `Mr. ${formattedPsoName}`;
    }

    const htmlBody = `
    <!DOCTYPE html>
    <html>
    <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>BAMS Purchase Order</title>
        <style>
            body { font-family: Arial, sans-serif; line-height: 1.8; color: #000000 !important; margin: 0; padding: 0; background: #ffffff; }
            * { color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; }
            .outer-wrapper { padding: 24px 0; }
            .container { max-width: 640px; margin: 0 auto; background: white; padding: 24px 24px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
            .header { text-align: left; margin-bottom: 30px; border-bottom: 2px solid #000000; padding-bottom: 12px; }
            .header h1 { margin: 0 0 10px 0; font-size: 24px; color: #000000 !important; font-weight: bold; }
            .form-section { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
            .form-group { margin-bottom: 15px; }
            .form-group label { display: block; font-weight: bold; margin-bottom: 5px; font-size: 12px; color: #000000 !important; }
            .form-group input, .form-group textarea { width: 100%; padding: 8px; border: 1px solid #000000; border-radius: 4px; font-size: 12px; box-sizing: border-box; color: #000000 !important; background-color: #ffffff !important; font-weight: normal; word-wrap: break-word; overflow-wrap: break-word; }
            .form-group input[readonly], .form-group textarea[readonly] { color: #000000 !important; background-color: #f5f5f5 !important; -webkit-text-fill-color: #000000 !important; }
            .form-group textarea { min-height: 60px; resize: vertical; }
            .form-group .hint { font-size: 10px; color: #000000 !important; margin-top: 3px; font-style: italic; }
            .items-section { margin-top: 30px; }
            .items-section h3 { margin-bottom: 15px; font-size: 16px; font-weight: bold; color: #000000 !important; }
            .items-table { width: 100%; border-collapse: collapse; margin-top: 10px; table-layout: auto; }
            .items-table th { background-color: #f0f0f0; font-weight: bold; padding: 10px 8px; text-align: left; border: 1px solid #000000; font-size: 11px; color: #000000 !important; word-wrap: break-word; }
            .items-table td { padding: 8px; border: 1px solid #000000; font-size: 11px; color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; max-width: 120px; }
            .letter-content { margin-top: 30px; line-height: 1.8; }
            .letter-content p { margin-bottom: 15px; color: #000000 !important; word-wrap: break-word; overflow-wrap: break-word; }
            .note-section { margin-top: 30px; padding: 15px; background-color: #f9f9f9; border-left: 4px solid #000000; font-style: italic; color: #000000 !important; }
            table { color: #000000 !important; }
            table td, table th { color: #000000 !important; }
            ol, li { color: #000000 !important; }
        </style>
    </head>
    <body>
        <div class="outer-wrapper">
        <div class="container">
            <div class="header">
                <h1>BAMS PURCHASE ORDER</h1>
            </div>
            
            <div class="form-section">
                <div>
                    <div class="form-group">
                        <label>Purchase Order Number</label>
                        <input type="text" value="${purchaseOrderNumber}" readonly style="background-color: #f5f5f5;" />
                    </div>
                    <div class="form-group">
                        <label>Purchase Order DATE</label>
                        <input type="text" value="${poDateFormatted}" readonly style="background-color: #f5f5f5;" />
                    </div>
                    <div class="form-group">
                        <label>Vendor Name</label>
                        <input type="text" value="${vendorCompany}" readonly style="background-color: #f5f5f5;" />
                    </div>
                    <div class="form-group">
                        <label>Vendor Address</label>
                        <textarea readonly style="background-color: #f5f5f5;">${vendorAddress}</textarea>
                    </div>
                    <div class="form-group">
                        <label>Contact Person</label>
                        <input type="text" value="${vendorContactPerson}" readonly style="background-color: #f5f5f5;" />
                    </div>
                    <div class="form-group">
                        <label>Delivery date</label>
                        <input type="text" value="${deliveryDate}" readonly style="background-color: #f5f5f5;" />
                        <div class="hint">Purchase Order date +7 Days (Auto calculate)</div>
                    </div>
                    <div class="form-group">
                        <label>Payment Terms</label>
                        <input type="text" value="Net Within 30 days upon submission of required documents days" readonly style="background-color: #f5f5f5;" />
                    </div>
                    <div class="form-group">
                        <label>Amount: Rs.</label>
                        <input type="text" value="${grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}" readonly style="background-color: #f5f5f5; font-weight: bold;" />
                    </div>
                </div>
                <div>
                    <div class="form-group">
                        <label>Branch/</label>
                        <input type="text" value="${branchName}" readonly style="background-color: #f5f5f5;" />
                    </div>
                    <div class="form-group">
                        <label>Branch/</label>
                        <input type="text" value="${branchCode}" readonly style="background-color: #f5f5f5;" />
                    </div>
                    <div class="form-group">
                        <label>Branch/</label>
                        <textarea readonly style="background-color: #f5f5f5;">${branchAddress}</textarea>
                    </div>
                    <div class="form-group">
                        <label>Buyer Name</label>
                        <input type="text" value="${buyerName}" readonly style="background-color: #f5f5f5;" />
                        <div class="hint">(Incharge who is assigned)</div>
                    </div>
                    <div class="form-group">
                        <label>Email ID</label>
                        <input type="text" value="${buyerEmail}" readonly style="background-color: #f5f5f5;" />
                        <div class="hint">(Incharge email who is assigned)</div>
                    </div>
                </div>
            </div>
            
            <div class="items-section">
                <h3>Item details:</h3>
                <table class="items-table">
                    <thead>
                        <tr>
                            <th>S. NO</th>
                            <th>BAMS Equipment Name</th>
                            <th>UOM</th>
                            <th>Qty</th>
                            <th>Make/Model</th>
                            <th>Unit Price</th>
                            <th>Price</th>
                            <th>Tax %</th>
                            <th>Tax Amount</th>
                            <th>Price with Tax</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${itemsTable}
                    </tbody>
                </table>
            </div>

            <div style="margin-top: 30px; text-align: right;">
                <table style="width: 100%; max-width: 400px; margin-left: auto; border-collapse: collapse;">
                    <tr>
                        <td style="padding: 8px; text-align: right; border: 1px solid #000000; font-size: 12px; font-weight: bold; color: #000000 !important;">Sub Total (Price):</td>
                        <td style="padding: 8px; text-align: right; border: 1px solid #000000; font-size: 12px; color: #000000 !important;">Rs. ${totalBaseAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; text-align: right; border: 1px solid #000000; font-size: 12px; font-weight: bold; color: #000000 !important;">Sales tax:</td>
                        <td style="padding: 8px; text-align: right; border: 1px solid #000000; font-size: 12px; color: #000000 !important;">Rs. ${totalTaxAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                    <tr>
                        <td style="padding: 8px; text-align: right; border: 1px solid #000000; font-size: 12px; font-weight: bold; background-color: #f0f0f0; color: #000000 !important;">Grand Total (Price with Tax):</td>
                        <td style="padding: 8px; text-align: right; border: 1px solid #000000; font-size: 12px; font-weight: bold; background-color: #f0f0f0; color: #000000 !important;">Rs. ${grandTotal.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    </tr>
                </table>
            </div>

            <div style="margin-top: 40px;">
                <h3 style="margin-bottom: 15px; font-size: 16px; font-weight: bold; color: #000000 !important;">Terms and Conditions:</h3>
                <ol style="margin-left: 20px; line-height: 1.8; font-size: 12px; color: #000000 !important;">
                    <li style="margin-bottom: 10px; color: #000000 !important;">All the hardware must comply with specifications as per approved SLA</li>
                    <li style="margin-bottom: 10px; color: #000000 !important;">Delivery must be completed and intimated by date mentioned above</li>
                    <li style="margin-bottom: 10px; color: #000000 !important;">PO Number must be mentioned on all invoices, delivery challan and acknowledgement of returned hardware form (if applicable)</li>
                    <li style="margin-bottom: 10px; color: #000000 !important;">Any delay lead to penalties or cancellation of Purchase order</li>
                    <li style="margin-bottom: 10px; color: #000000 !important;">Complete and proper Delivery Challan (Make / Quantity / Serial No.) should be properly filled and signed from relevant vendor and branch staff.</li>
                    <li style="margin-bottom: 10px; color: #000000 !important;">In case of replacement of hardware acknowledgement of returned hardware form must be submitted with the invoices</li>
                </ol>
            </div>

            <div style="margin-top: 30px;">
                <h3 style="margin-bottom: 15px; font-size: 16px; font-weight: bold; color: #000000 !important;">Please Note:</h3>
                <ol style="margin-left: 20px; line-height: 1.8; font-size: 12px; color: #000000 !important;">
                    <li style="margin-bottom: 10px; color: #000000 !important;">Payment will be made as per the above mentioned hardware only; if any additional hardware would be required then PSO will take separate approval of these items from concerned management prior to invoicing</li>
                    <li style="margin-bottom: 10px; color: #000000 !important;">HBL reserves rights to cancel the subject Work Order if the installation is not completed within the stipulated time. The branch may be allocated to other vendor in case of non-compliance of SLA</li>
                </ol>
            </div>

            <div class="letter-content" style="margin-top: 30px;">
                <p style="margin-bottom: 20px; color: #000000 !important;">Dear ${formattedContactName}, M/S ${vendorCompany}</p>
                
                <p style="margin-bottom: 15px; color: #000000 !important;">
                    In the light of existing SLA, please procure and install the above mentioned BAMS Hardware and cable at HBL Branch Code ${branchCode} ${branchAddress} by ${deliveryDate}
                </p>
                
                ${branchPsoName ? `
                <p style="margin-bottom: 20px; color: #000000 !important;">
                    Dear ${formattedPsoName}${branchPsoRegion && branchPsoRegion !== 'Region' && !branchPsoRegion.toLowerCase().includes('pso') ? `, ${branchPsoRegion}` : ''}
                </p>
                
                <p style="margin-bottom: 20px; color: #000000 !important;">
                    Please coordinate with branch and vendor for installation of all BAMS equipment as per SLA by M/S ${vendorCompany}. In case of requirement of any additional equipment, kindly get approval from Head Security for further work processing.
                </p>
                ` : ''}
                </div>
            <div style="margin-top: 30px; font-size: 12px; color: #000000 !important;">
                <strong>${buyerName || 'HBL Support'}</strong>
            </div>
        </div>
        </div>
    </body>
    </html>
    `;

    return htmlBody;
};

